import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'

export interface PositionDutyItem {
  text: string
  category?: 'duties' | 'requirements' | 'skills'
}

export interface Position {
  id: string
  tenant_id: string
  name: string
  organization_id?: string | null
  department_id?: string | null
  code_1c?: string | null
  can_manage_schedule?: boolean
  description?: string | null
  duties?: PositionDutyItem[]
  created_at: string
  updated_at: string
  // Backend returns 'organizations' (Prisma relation name)
  organizations?: {
    id: string
    name: string
    bin?: string
  } | null
  departments?: {
    id: string
    name: string
  } | null
  employees?: {
    id: string
    first_name: string
    last_name: string
    employee_number: string
  }[]
  // Количество активных сотрудников на должности (считает бэкенд)
  employees_count?: number
}

export interface PositionFilters {
  search?: string
  department_id?: string
  page?: number
  limit?: number
  sort?: string
}

export interface PaginatedPositions {
  data: Position[]
  total: number
  page: number
  limit: number
  totalPages: number
  from1cCount: number
  localCount: number
}

const POSITIONS_QUERY_KEY = 'positions'

export const usePositions = (filters: PositionFilters = {}) => {
  return useQuery({
    queryKey: [POSITIONS_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedPositions> => {
      const response = await apiClient.getPositions(filters)
      // Приводим ответ API к нужному формату
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
        from1cCount: response.from1cCount || 0,
        localCount: response.localCount || 0,
      }
    },
    staleTime: 30 * 1000, // 30 seconds
    retry: 3,
  })
}

export const usePosition = (id: string) => {
  return useQuery({
    queryKey: [POSITIONS_QUERY_KEY, id],
    queryFn: async (): Promise<Position> => {
      const response = await apiClient.getPosition(id)
      return response.data
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  })
}

export const useCreatePosition = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: Partial<Position>) => {
      const response = await apiClient.createPosition(data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POSITIONS_QUERY_KEY] })
    },
  })
}

export const useUpdatePosition = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Position> }) => {
      const response = await apiClient.updatePosition(id, data)
      return response.data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [POSITIONS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [POSITIONS_QUERY_KEY, id] })
    },
  })
}

export const useDeletePosition = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.deletePosition(id)
      // Для DELETE операций response может быть null (код 204)
      return response?.data || response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POSITIONS_QUERY_KEY] })
    },
  })
}